import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar } from '../components/ui/calendar';
import { Loader2, CheckCircle2, Clock, Video, MapPin, ArrowLeft, CalendarDays, XCircle, CalendarClock, CalendarPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENQUIRY_OPTIONS = [
  'SEO (Search Engine Optimisation)',
  'PPC / Google Ads',
  'Social Media Marketing',
  'Content Marketing',
  'Email Marketing',
  'Web Design & Development',
  'Branding & Design',
  'Digital Strategy & Consultancy',
  'Other',
];

const PublicBooking = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const cancelId = searchParams.get('cancel');
  const rescheduleId = searchParams.get('reschedule');
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', company_name: '', company_website: '', nature_of_enquiry: '', phone: '', notes: '' });
  const [hp, setHp] = useState('');
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  // reschedule / cancel
  const [existingBooking, setExistingBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const allowedDays = page?.availability?.days || [1, 2, 3, 4, 5];
  const enquiryOptions = (page?.enquiry_options && page.enquiry_options.length) ? page.enquiry_options : ENQUIRY_OPTIONS;

  // Build one-click "Add to calendar" URLs from the selected slot.
  const buildCalendarLinks = () => {
    if (!selectedSlot || !page) return null;
    try {
      const start = new Date(selectedSlot.start);
      const end = new Date(selectedSlot.end);
      const z = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      const title = encodeURIComponent(page.name || 'Meeting');
      const loc = encodeURIComponent(done?.join_url || page.location || '');
      const details = encodeURIComponent(done?.join_url ? `${page.name} - Join: ${done.join_url}` : (page.name || ''));
      const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${z(start)}/${z(end)}&details=${details}&location=${loc}`;
      const outlook = `https://outlook.office.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${title}&startdt=${encodeURIComponent(start.toISOString())}&enddt=${encodeURIComponent(end.toISOString())}&body=${details}&location=${loc}`;
      return { google, outlook };
    } catch { return null; }
  };

  const formatWhen = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  useEffect(() => {
    axios.get(`${API}/public/booking-pages/${id}`)
      .then(res => {
        setPage(res.data);
        // default to next allowed day
        const days = res.data.availability?.days || [1, 2, 3, 4, 5];
        let d = new Date(); d.setHours(0, 0, 0, 0);
        for (let i = 0; i < 14; i++) {
          const iso = d.getDay() === 0 ? 7 : d.getDay();
          if (days.includes(iso) && d > new Date()) { setDate(new Date(d)); break; }
          d.setDate(d.getDate() + 1);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const fetchSlots = useCallback(async (d) => {
    if (!d) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    try {
      const dayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const res = await axios.get(`${API}/public/booking-pages/${id}/slots`, { params: { day: dayStr } });
      setSlots(res.data.slots || []);
    } catch { setSlots([]); }
    finally { setSlotsLoading(false); }
  }, [id]);

  useEffect(() => { if (date) fetchSlots(date); }, [date, fetchSlots]);

  // Load the existing booking when arriving via a reschedule/cancel link
  useEffect(() => {
    const bid = cancelId || rescheduleId;
    if (!bid) return;
    axios.get(`${API}/public/booking-pages/bookings/${bid}`)
      .then(res => {
        setExistingBooking(res.data);
        if (res.data.status === 'cancelled') setCancelled(true);
        if (rescheduleId) {
          const fn = (res.data.full_name || '').split(' ');
          setForm(f => ({ ...f, first_name: fn[0] || '', last_name: fn.slice(1).join(' ') || '', email: res.data.email || '' }));
        }
      })
      .catch(() => setExistingBooking(null));
  }, [cancelId, rescheduleId]);

  const cancelBooking = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/public/booking-pages/bookings/${cancelId}/cancel`);
      setCancelled(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not cancel. Please contact the organiser.');
    } finally { setCancelling(false); }
  };

  const book = async (e) => {
    e.preventDefault();
    setError('');
    setBooking(true);
    try {
      const res = await axios.post(`${API}/public/booking-pages/${id}/book`, {
        ...form, start: selectedSlot.start, hp,
        base_url: window.location.origin,
        reschedule_of: rescheduleId || undefined,
      });
      setDone(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not book that time. Please try another.');
    } finally { setBooking(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-7 h-7 animate-spin text-violet-600" /></div>;
  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="booking-notfound"><p className="text-slate-500">This booking page is no longer available.</p></div>;

  const dayDisabled = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    const iso = d.getDay() === 0 ? 7 : d.getDay();
    return !allowedDays.includes(iso);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-start sm:items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden" data-testid="public-booking">
        {cancelId ? (
          <div className="text-center py-16 px-8" data-testid="booking-cancel">
            {cancelled ? (
              <>
                <XCircle className="w-16 h-16 text-rose-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">Meeting cancelled</h2>
                <p className="text-sm text-slate-500">Your meeting has been cancelled. You can book a new time whenever you like.</p>
              </>
            ) : (
              <>
                <CalendarClock className="w-14 h-14 text-slate-400 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">Cancel this meeting?</h2>
                {existingBooking ? (
                  <p className="text-sm text-slate-500 mb-6">{existingBooking.page_name} - <b>{formatWhen(existingBooking.start)}</b></p>
                ) : (
                  <p className="text-sm text-slate-400 mb-6">Loading your booking…</p>
                )}
                {error && <p className="text-sm text-rose-600 mb-3" data-testid="cancel-error">{error}</p>}
                <button onClick={cancelBooking} disabled={cancelling || !existingBooking} className="bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg px-5 py-2.5 text-sm inline-flex items-center gap-2 transition-colors disabled:opacity-60" data-testid="cancel-booking-confirm">
                  {cancelling ? <><Loader2 className="w-4 h-4 animate-spin" /> Cancelling…</> : 'Yes, cancel meeting'}
                </button>
              </>
            )}
          </div>
        ) : done ? (
          <div className="text-center py-16 px-8" data-testid="booking-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">You're booked in</h2>
            <p className="text-sm text-slate-500 max-w-sm mx-auto">{done.message}</p>
            {done.join_url && (
              <a href={done.join_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors">
                <Video className="w-4 h-4" /> Join Teams meeting
              </a>
            )}
            {(() => {
              const cal = buildCalendarLinks();
              if (!cal) return null;
              return (
                <div className="mt-6" data-testid="add-to-calendar">
                  <p className="text-xs text-slate-400 mb-2">Add to your calendar</p>
                  <div className="flex items-center justify-center gap-2.5 flex-wrap">
                    <a href={cal.google} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2.5 border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-colors" data-testid="add-google-calendar">
                      <CalendarPlus className="w-4 h-4 text-[#1a73e8]" /> Google Calendar
                    </a>
                    <a href={cal.outlook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2.5 border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-colors" data-testid="add-outlook-calendar">
                      <CalendarPlus className="w-4 h-4 text-[#0f6cbd]" /> Outlook
                    </a>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="grid md:grid-cols-[260px_1fr]">
            {/* Left: meeting info */}
            <div className="bg-slate-50 border-r border-slate-100 p-6">
              <h1 className="text-xl font-semibold text-slate-900 mb-2">{page.name}</h1>
              {page.description && <p className="text-sm text-slate-500 mb-4">{page.description}</p>}
              <div className="space-y-2 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> {page.duration_min} minutes</p>
                {page.online_meeting ? (
                  <p className="flex items-center gap-2"><Video className="w-4 h-4 text-slate-400" /> Microsoft Teams</p>
                ) : page.location ? (
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {page.location}</p>
                ) : null}
                <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-slate-400" /> {page.availability?.timezone || 'Europe/London'}</p>
              </div>
            </div>

            {/* Right: calendar/slots or details */}
            <div className="p-6">
              {rescheduleId && existingBooking && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-800 flex items-start gap-2" data-testid="reschedule-banner">
                  <CalendarClock className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Rescheduling your meeting from <b>{formatWhen(existingBooking.start)}</b>. Pick a new time below - your old slot will be released.</span>
                </div>
              )}
              {!selectedSlot ? (
                <div className="grid sm:grid-cols-[auto_1fr] gap-5">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={dayDisabled} className="rounded-md" data-testid="booking-calendar" />
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      {date ? date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Pick a date'}
                    </p>
                    <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1" data-testid="booking-slots">
                      {slotsLoading ? (
                        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                      ) : slots.length === 0 ? (
                        <p className="text-sm text-slate-400 py-4">No times available. Try another day.</p>
                      ) : slots.map(s => (
                        <button key={s.start} onClick={() => setSelectedSlot(s)} className="w-full text-sm border border-slate-200 hover:border-violet-500 hover:text-violet-700 text-slate-700 rounded-lg py-2 font-medium transition-colors" data-testid={`slot-${s.label}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={book} className="space-y-3 max-w-md">
                  <button type="button" onClick={() => setSelectedSlot(null)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-1" data-testid="back-to-slots"><ArrowLeft className="w-4 h-4" /> Back</button>
                  <div className="bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 text-sm text-violet-800">
                    {date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} at <b>{selectedSlot.label}</b>
                  </div>
                  <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">First name *</label>
                      <input required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/40" data-testid="booking-first-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Last name *</label>
                      <input required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/40" data-testid="booking-last-name" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/40" data-testid="booking-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Company name *</label>
                    <input required value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/40" data-testid="booking-company-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Company website *</label>
                    <input required type="text" inputMode="url" placeholder="www.example.com" value={form.company_website} onChange={e => setForm({ ...form, company_website: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40" data-testid="booking-company-website" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nature of enquiry *</label>
                    <select required value={form.nature_of_enquiry} onChange={e => setForm({ ...form, nature_of_enquiry: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/40" data-testid="booking-nature-of-enquiry">
                      <option value="" disabled>Select an option…</option>
                      {enquiryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                    <input required type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/40" data-testid="booking-phone" />
                  </div>
                  {page.collect_notes && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Anything we should know?</label>
                      <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/40" data-testid="booking-notes" />
                    </div>
                  )}
                  {error && <p className="text-sm text-rose-600" data-testid="booking-error">{error}</p>}
                  <button type="submit" disabled={booking} className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-lg py-2.5 text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="confirm-booking-btn">
                    {booking ? <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</> : 'Confirm booking'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicBooking;
